import React, { useState } from 'react'
import { IconButton, styled } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { CheckIcon } from '~assets/icons'
import { FormHelperText } from '~components/common'
import { Icon } from '~components/internal'

import './common.less'

type TextAreaProps = {
  value?: string
  label?: string
  placeholder?: string
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  error?: boolean
  helperText?: string
  readOnly?: boolean
  onFocus?: () => void
  onBlur?: () => void
  'aria-label'?: string
  showCopyButton?: boolean
  id?: string
}

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  padding: '4px',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const TextArea: React.FC<TextAreaProps> = ({
  value = '',
  label,
  placeholder,
  onChange,
  error,
  helperText,
  readOnly = false,
  'aria-label': ariaLabel,
  onFocus,
  onBlur,
  showCopyButton = false,
  id,
}) => {
  const [copied, setCopied] = useState<boolean>(false)
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onChange?.(e)
  }
  const theme = useTheme()

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value.toString())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  return (
    <div style={{ width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {label && (
        <label htmlFor={id} className='label-large'>
          {label}
        </label>
      )}
      <textarea
        id={id}
        className={`textarea ${readOnly ? 'textarea-readonly' : 'textarea-blue-border-on-focus'}`}
        value={value ?? ''}
        onChange={handleTextChange}
        placeholder={placeholder}
        rows={10}
        cols={50}
        disabled={readOnly}
        aria-label={ariaLabel}
        aria-invalid={error}
        aria-describedby={helperText ? `${id}-helper-text` : undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        style={{
          boxShadow: error
            ? `inset 0 0 0 2px ${theme.palette.error.main}`
            : readOnly
              ? `inset 0 0 0 1px ${theme.palette.input.disabled}`
              : `inset 0 0 0 1px ${theme.palette.input.default}`,
          borderRadius: 4,
          fontSize: 16,
          fontFamily: theme.typography.body2.fontFamily,
          transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
          cursor: readOnly ? 'default' : 'text',
          color: readOnly ? theme.palette.text.secondary : theme.palette.text.primary,
          backgroundColor: readOnly ? theme.palette.action.disabledInput : undefined,
          paddingRight: readOnly && showCopyButton ? '32px' : undefined,
          paddingLeft: readOnly && showCopyButton ? '12px' : undefined,
        }}
      />
      {readOnly && showCopyButton && (
        <CopyButton
          onClick={handleCopy}
          size='small'
          aria-label='copy text'
          style={{
            position: 'absolute',
            top: label ? '46px' : '22px',
            zIndex: 1,
          }}
        >
          {copied ? <CheckIcon width={16} /> : <Icon name='copy' fontSize='small' fill={0} />}
        </CopyButton>
      )}
      {helperText && (
        <FormHelperText id={`${id}-helper-text`} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  )
}

export default TextArea
